import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Loader2, Cloud, Music, Heart, Sparkles, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import AuthCard from "@/components/AuthCard";

const SignIn = () => {
  const { user, login, signup, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(location.state?.mode === "signup" ? "signup" : "login");

  useEffect(() => {
    if (user && user.user_id) navigate("/", { replace: true });
  }, [user, navigate]);
  const [form, setForm] = useState({ name: "", family_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login({ email: form.email, password: form.password });
      } else {
        await signup(form);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#FFFDF6] px-4 py-16" data-testid="signin-page">
      {/* playful universe backdrop */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#FEF3C7] blur-3xl" />
        <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#E0F2FE] blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-[#DCFCE7] blur-3xl" />
        <Star className="animate-float-soft absolute left-[10%] top-[18%] h-9 w-9 fill-[#FFC107] text-[#FFC107]" />
        <Cloud className="animate-float-soft-delayed absolute right-[12%] top-[16%] h-14 w-14 fill-white text-white drop-shadow-md" />
        <Music className="animate-float-soft absolute bottom-[22%] left-[14%] h-7 w-7 text-[#9333EA]" />
        <Sparkles className="animate-float-soft-delayed absolute right-[18%] bottom-[30%] h-8 w-8 text-[#FF6B6B]" />
        <Heart className="animate-float-soft absolute left-[24%] top-[56%] h-6 w-6 fill-[#FF6B6B]/70 text-[#FF6B6B]" />
        <div className="rainbow-arc absolute -bottom-40 left-1/2 h-[26rem] w-[48rem] -translate-x-1/2 opacity-60" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="mb-8 text-center">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#FFC107] shadow-[0_14px_28px_-6px_rgba(255,193,7,0.5)]">
            <Star className="h-8 w-8 fill-white text-white" />
          </span>
          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Little Songs. <span className="bg-gradient-to-r from-[#FFC107] via-[#FF6B6B] to-[#9333EA] bg-clip-text text-transparent">Big Faith.</span>
          </h1>
          <p className="mt-2 text-sm text-slate-500 sm:text-base">
            Songs they'll love. Truths they'll remember.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#E2DDD0] bg-white/80 px-4 py-1.5 text-xs font-bold text-slate-500 backdrop-blur">
            <ShieldCheck className="h-3.5 w-3.5 text-[#16A34A]" />
            Grown-ups sign in — little ones just sing along
          </p>
        </div>

        <AuthCard initialMode={location.state?.mode === "signup" ? "signup" : "login"} />
        <p className="mt-5 text-center font-hand text-xl text-slate-400">
          made for little hearts, loved by grown-ups
        </p>
      </motion.div>
    </div>
  );
};

export default SignIn;
